import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Modal, Textarea, useToast } from '@koyomi/ui';
import { api, ApiError } from '../../lib/api.ts';
import { useAuthStore } from '../../app/auth-store.ts';
import './instruction.css';

interface PreviewAction { action: { type: string }; title: string; detail: string | null; affected?: { appointmentId: string; clientName: string; current: string; offer: string; substituteNote: string | null }[] }
interface Preview { id: string; interpretation: string; actions: PreviewAction[]; clarification: string | null; expiresAt: string }

/** Instrução livre (mockup 03): a IA mostra o que entendeu e as ações; nada roda sem confirmação. */
export function InstructionModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation(['calendar', 'common']);
  const toast = useToast();
  const qc = useQueryClient();
  const businessId = useAuthStore((s) => s.businessId);
  const [text, setText] = useState('');
  const [preview, setPreview] = useState<Preview | null>(null);
  const [busy, setBusy] = useState(false);
  const run = async (fn: () => Promise<void>) => { setBusy(true); try { await fn(); } catch (err) { toast.push(err instanceof ApiError ? err.message : t('common:errors.INTERNAL'), 'danger'); } finally { setBusy(false); } };
  const ask = () => run(async () => { setPreview(await api<Preview>('/business/instructions', { body: { instruction: text.trim() } })); });
  const confirm = () => run(async () => {
    const r = await api<{ status: string; result: { results: { ok: boolean }[] } }>(`/business/instructions/${preview!.id}/confirm`, { method: 'POST' });
    await Promise.all(['calendar', 'waitlist', 'member-blocks'].map((k) => qc.invalidateQueries({ queryKey: [businessId, k] })));
    toast.push(r.status === 'EXECUTED' ? t('calendar:instruction.done', { count: r.result?.results?.filter((x) => x.ok).length ?? 0 }) : t('calendar:instruction.failed'), r.status === 'EXECUTED' ? 'success' : 'danger');
    onClose();
  });
  const edit = () => run(async () => { if (preview) await api(`/business/instructions/${preview.id}/reject`, { method: 'POST' }).catch(() => undefined); setPreview(null); });
  const hasActions = !!preview && preview.actions.length > 0;
  return (
    <Modal open onClose={onClose} title={<span className="instr__head"><span>{t('calendar:instruction.title')}</span><span className="k-muted instr__hint">{t('calendar:instruction.nothingWithout')}</span></span>} size="lg"
      footer={preview ? <><span className="k-muted instr__foot">{t('calendar:instruction.cost')}</span><span style={{ flex: 1 }} /><Button onClick={edit} disabled={busy}>{t('calendar:instruction.edit')}</Button>{hasActions && <Button variant="primary" loading={busy} onClick={confirm}>{t('calendar:instruction.confirm')}</Button>}</> : <><Button onClick={onClose}>{t('common:actions.cancel')}</Button><Button variant="primary" loading={busy} disabled={text.trim().length < 3} onClick={ask}>{t('calendar:instruction.preview')}</Button></>}>
      {!preview ? (
        <Textarea rows={4} autoFocus value={text} onChange={(e) => setText(e.target.value)} placeholder={t('calendar:instruction.placeholder')} />
      ) : (
        <div className="instr">
          <div className="instr__text">{preview.interpretation || text}</div>
          {preview.clarification && <div className="instr__note instr__note--warn">{preview.clarification}</div>}
          {hasActions && (
            <>
              <div className="k-section-label">{t('calendar:instruction.plan')}</div>
              <ol className="instr__list">
                {preview.actions.map((a, i) => (
                  <li key={i} className="instr__item">
                    <span className="instr__num">{i + 1}</span>
                    <div className="instr__body">
                      <div className="instr__title">{a.title}</div>
                      {a.detail && <div className="k-muted instr__detail">{a.detail}</div>}
                      {a.affected && a.affected.length > 0 && (
                        <table className="instr__table">
                          <thead><tr><th>{t('calendar:instruction.cols.client')}</th><th>{t('calendar:instruction.cols.current')}</th><th>{t('calendar:instruction.cols.offer')}</th></tr></thead>
                          <tbody>{a.affected.map((x) => <tr key={x.appointmentId}><td><strong>{x.clientName}</strong></td><td>{x.current}</td><td>{x.offer}{x.substituteNote && <span className="k-muted"> · {x.substituteNote}</span>}</td></tr>)}</tbody>
                        </table>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
              <div className="instr__note">{t('calendar:instruction.guarantee')}</div>
            </>
          )}
        </div>
      )}
    </Modal>
  );
}
